import { useEffect, useMemo, useRef, useState } from 'react';
import { cx } from '../cx';

/**
 * Grafo de centros e pontos: cada centro (`hub`) junta os pontos (`node`) ligados a ele, e um ponto ligado a mais de um
 * centro fica entre eles. Todo ponto e toda linha é um dado: nada é desenhado para enfeitar.
 * A posição sai de regra (forças com semente fixa pelo id), então o mesmo conjunto cai sempre no mesmo lugar.
 * Serve aos dois temas: só usa tokens.
 */
/** `ring`: um anel vazado, para o que é de outra natureza que os pontos (no Rastro, o sinal do consumidor). */
export type GraphShape = 'dot' | 'square' | 'diamond' | 'triangle' | 'ring';
/** `since`: a partir de quando o ponto existe (qualquer número que cresce com o tempo). `named`: o nome fica sempre à vista. */
export type GraphNode = { id: string; kind: 'hub' | 'node'; label: string; sub?: string; weight?: number; faint?: boolean; since?: number; shape?: GraphShape; named?: boolean };
export type GraphLink = { source: string; target: string; weight?: number; since?: number };
/** Centros que são partes do mesmo assunto: ficam perto uns dos outros, dentro de um contorno com nome. */
export type GraphGroup = { id: string; label: string; sub?: string; members: string[] };
type P = { x: number; y: number };

const hash = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; };
const rng = (seed: number) => () => { seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };

/** Onde cada ponto fica. Centros se afastam entre si, cada ponto é puxado pelos centros a que se liga, e o que não tem ligação ocupa os vãos. */
export function graphLayout(nodes: GraphNode[], links: GraphLink[], groups: GraphGroup[] = []): Map<string, P> {
  const n = nodes.length;
  const at = new Map(nodes.map((d, i) => [d.id, i]));
  const hub = nodes.map((d) => d.kind === 'hub');
  const deg = new Array<number>(n).fill(0);
  const pairs = links.map((l) => [at.get(l.source), at.get(l.target), l.weight ?? 1] as const).filter((l): l is readonly [number, number, number] => l[0] !== undefined && l[1] !== undefined);
  for (const [a, b] of pairs) { deg[a]!++; deg[b]!++; }
  const x = new Array<number>(n).fill(0); const y = new Array<number>(n).fill(0);
  nodes.forEach((d, i) => { const r = rng(hash(d.id)); const a = r() * Math.PI * 2; const rad = Math.sqrt(r()) * (hub[i] ? 900 : 1300); x[i] = Math.cos(a) * rad * 1.5; y[i] = Math.sin(a) * rad; });
  // o ponto começa perto do primeiro centro a que se liga: o resultado não depende da ordem em que os dados chegaram
  for (const [a, b] of pairs) { const [h, p] = hub[a] ? [a, b] : [b, a]; if (hub[h] && !hub[p] && deg[p] === 1) { const r = rng(hash(nodes[p]!.id)); const ang = r() * Math.PI * 2; x[p] = x[h]! + Math.cos(ang) * 70; y[p] = y[h]! + Math.sin(ang) * 70; } }
  const k = nodes.map((_, i) => (hub[i] ? 520 : deg[i] ? 40 : 95));
  const packs = groups.map((g) => g.members.map((id) => at.get(id)).filter((i): i is number => i !== undefined && hub[i]!)).filter((m) => m.length > 1);
  const STEPS = 520;
  const dx = new Array<number>(n); const dy = new Array<number>(n);
  for (let s = 0; s < STEPS; s++) {
    const temp = 70 * (1 - s / STEPS) + 0.4;
    dx.fill(0); dy.fill(0);
    for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
      let ux = x[i]! - x[j]!; let uy = y[i]! - y[j]!;
      // o texto dos centros é largo: a distância na horizontal conta menos, então eles se espalham mais para os lados
      const wide = hub[i] && hub[j] ? 0.55 : 1;
      let d2 = ux * ux * wide * wide + uy * uy;
      if (!(hub[i] && hub[j]) && d2 > 810000) continue;
      if (d2 < 1) { ux = (i % 7) - 3 || 1; uy = (j % 5) - 2 || 1; d2 = ux * ux + uy * uy; }
      const d = Math.sqrt(d2); const f = (k[i]! * k[j]!) / d2;
      dx[i]! += (ux / d) * f; dy[i]! += (uy / d) * f; dx[j]! -= (ux / d) * f; dy[j]! -= (uy / d) * f;
    }
    for (const [a, b] of pairs) {
      const ux = x[a]! - x[b]!; const uy = y[a]! - y[b]!; const d = Math.sqrt(ux * ux + uy * uy) || 1;
      const h = hub[a] ? a : b; const p = hub[a] ? b : a;
      const len = 46 + 13 * Math.sqrt(deg[h]!);
      const f = (d * d) / len / Math.max(1, deg[p]! * 0.8);
      dx[a]! -= (ux / d) * f * (hub[a] ? 0.04 : 1); dy[a]! -= (uy / d) * f * (hub[a] ? 0.04 : 1);
      dx[b]! += (ux / d) * f * (hub[b] ? 0.04 : 1); dy[b]! += (uy / d) * f * (hub[b] ? 0.04 : 1);
    }
    // centros da mesma família se puxam para o meio dela
    for (const m of packs) { const cx0 = m.reduce((a, i) => a + x[i]!, 0) / m.length; const cy0 = m.reduce((a, i) => a + y[i]!, 0) / m.length; for (const i of m) { dx[i]! -= (x[i]! - cx0) * 3.2; dy[i]! -= (y[i]! - cy0) * 3.2; } }
    for (let i = 0; i < n; i++) {
      const g = hub[i] ? 0.5 : deg[i] ? 0.02 : 0.2;
      dx[i]! -= x[i]! * g * 0.62; dy[i]! -= y[i]! * g;
      const m = Math.sqrt(dx[i]! * dx[i]! + dy[i]! * dy[i]!) || 1; const step = Math.min(m, temp);
      x[i]! += (dx[i]! / m) * step; y[i]! += (dy[i]! / m) * step;
    }
  }
  // o nome de cada centro ocupa uma caixa larga embaixo dele: centros com a caixa sobreposta se afastam, e levam junto os pontos que são só deles
  const hubs = nodes.map((_, i) => i).filter((i) => hub[i]);
  if (hubs.length > 1) {
    const own = new Map<number, number[]>(hubs.map((h) => [h, []]));
    for (const [a, b] of pairs) { const h = hub[a] ? a : b; const p = hub[a] ? b : a; if (deg[p] === 1) own.get(h)?.push(p); }
    const xs = hubs.map((h) => x[h]!); const ys = hubs.map((h) => y[h]!);
    const area = (Math.max(...xs) - Math.min(...xs)) * (Math.max(...ys) - Math.min(...ys));
    const bh = Math.sqrt((0.8 * area) / (hubs.length * 2.3)); const bw = bh * 2.3;
    const move = (h: number, mx: number, my: number) => { x[h]! += mx; y[h]! += my; for (const p of own.get(h)!) { x[p]! += mx; y[p]! += my; } };
    for (let s = 0; s < 120; s++) {
      let hit = false;
      for (let a = 0; a < hubs.length; a++) for (let b = a + 1; b < hubs.length; b++) {
        const i = hubs[a]!; const j = hubs[b]!;
        const ox = bw - Math.abs(x[i]! - x[j]!); const oy = bh - Math.abs(y[i]! - y[j]!);
        if (ox <= 0 || oy <= 0) continue;
        hit = true;
        if (ox / bw < oy / bh) { const m = (ox / 2 + 1) * (x[i]! < x[j]! ? -1 : 1); move(i, m, 0); move(j, -m, 0); }
        else { const m = (oy / 2 + 1) * (y[i]! < y[j]! ? -1 : 1); move(i, 0, m); move(j, 0, -m); }
      }
      if (!hit) break;
    }
  }
  return new Map(nodes.map((d, i) => [d.id, { x: Math.round(x[i]!), y: Math.round(y[i]!) }]));
}

type View = { k: number; x: number; y: number };
const box = (pts: P[]) => { const xs = pts.map((p) => p.x); const ys = pts.map((p) => p.y); const x0 = Math.min(...xs); const x1 = Math.max(...xs); const y0 = Math.min(...ys); const y1 = Math.max(...ys); return { x0, y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) }; };
/** O contorno de um conjunto de pontos (casco convexo); a folga em volta é a espessura do traço. */
const hull = (pts: P[]) => {
  const p = [...pts].sort((a, b) => a.x - b.x || a.y - b.y);
  if (p.length < 3) return p;
  const turn = (o: P, a: P, b: P) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const half = (list: P[]) => { const h: P[] = []; for (const q of list) { while (h.length > 1 && turn(h[h.length - 2]!, h[h.length - 1]!, q) <= 0) h.pop(); h.push(q); } h.pop(); return h; };
  return [...half(p), ...half([...p].reverse())];
};

export function Graph({ nodes, links, groups = [], selected, onSelect, note, shapeIn, until, fresh, path, pulses, className }: {
  nodes: GraphNode[];
  links: GraphLink[];
  groups?: GraphGroup[];
  /** Um centro, um ponto ou um grupo. */
  selected?: string | null;
  onSelect?: (id: string | null) => void;
  /** Texto curto ao lado de um ponto em destaque: o que o liga ao centro escolhido. */
  note?: (nodeId: string, hubId: string) => string | undefined;
  /** A forma do ponto dentro do centro escolhido, quando é diferente da forma dele no conjunto. */
  shapeIn?: (nodeId: string, hubId: string) => GraphShape | undefined;
  /** Só aparece o que existe até aqui (`since` de pontos e linhas). */
  until?: number;
  /** O que existe só depois daqui é novo: a linha se acende do ponto até o centro quando aparece. */
  fresh?: number;
  /** Uma rota entre dois pontos, na ordem: as ligações se acendem uma depois da outra. Vale mais que `selected`. */
  path?: string[];
  /** Ligações por onde a luz passa, uma depois da outra, na ordem dada, e recomeça: cada passagem é um dado (um movimento), nunca enfeite. */
  pulses?: { source: string; target: string }[];
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hot, setHot] = useState<string | null>(null);
  const [free, setFree] = useState<View | null>(null); // a pessoa moveu ou aproximou: vale até a próxima escolha
  const [moving, setMoving] = useState(false);
  const [ready, setReady] = useState(false); // a primeira posição não é animada
  const shape = nodes.map((d) => `${d.kind}:${d.id}`).join('|') + '#' + links.map((l) => `${l.source}>${l.target}`).join('|') + '#' + groups.map((g) => `${g.id}:${g.members.join(',')}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const base = useMemo(() => graphLayout(nodes, links, groups), [shape]);
  const near = useMemo(() => { const m = new Map<string, Set<string>>(); for (const l of links) { (m.get(l.source) ?? m.set(l.source, new Set()).get(l.source)!).add(l.target); (m.get(l.target) ?? m.set(l.target, new Set()).get(l.target)!).add(l.source); } return m; }, [links]);
  const byId = useMemo(() => new Map(nodes.map((d) => [d.id, d])), [nodes]);
  const later = (since?: number) => until !== undefined && since !== undefined && since > until;

  useEffect(() => { const el = ref.current; if (!el) return; const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight })); ro.observe(el); return () => ro.disconnect(); }, []);
  useEffect(() => { setFree(null); }, [selected]);
  useEffect(() => { if (!size.w || ready) return; const t = setTimeout(() => setReady(true), 60); return () => clearTimeout(t); }, [size.w, ready]);

  const route = path && path.length > 1 && path.every((id) => base.has(id)) ? path : null;
  const focus = !route && selected && base.has(selected) ? selected : null;
  const group = !route && selected ? groups.find((g) => g.id === selected) ?? null : null;
  const hop = (l: GraphLink) => { if (!route) return -1; for (let i = 1; i < route.length; i++) if ((route[i - 1] === l.source && route[i] === l.target) || (route[i - 1] === l.target && route[i] === l.source)) return i - 1; return -1; };
  const isNew = (since?: number) => fresh !== undefined && since !== undefined && since > fresh && !later(since);
  // centro escolhido: os pontos dele abrem em roda, na mesma ordem em que estavam, para os nomes caberem. o resto não sai do lugar
  const pos = useMemo(() => {
    if (!focus || byId.get(focus)?.kind !== 'hub') return base;
    const c = base.get(focus)!; const around = [...(near.get(focus) ?? [])].filter((id) => base.has(id));
    if (!around.length) return base;
    const ang = (id: string) => Math.atan2(base.get(id)!.y - c.y, base.get(id)!.x - c.x);
    const order = around.sort((a, b) => ang(a) - ang(b));
    const radius = Math.max(150, order.length * 26); const start = ang(order[0]!);
    const next = new Map(base);
    order.forEach((id, i) => { const a = start + (i / order.length) * Math.PI * 2; next.set(id, { x: Math.round(c.x + Math.cos(a) * radius * 1.25), y: Math.round(c.y + Math.sin(a) * radius) }); });
    return next;
  }, [base, focus, near, byId]);
  // o que está aceso: o escolhido (ou o grupo escolhido); sem escolha, o que está sob o cursor
  const cores = useMemo(() => (route ? [] : group ? group.members.filter((id) => base.has(id)) : focus ? [focus] : hot && base.has(hot) ? [hot] : []), [route, group, focus, hot, base]);
  const on = useMemo(() => (route ? new Set(route) : cores.length ? new Set(cores.flatMap((id) => [id, ...(near.get(id) ?? [])])) : null), [route, cores, near]);

  const fit = (ids: string[], pad: number, max: number): View => {
    const b = box(ids.map((id) => pos.get(id)!).filter(Boolean));
    const k = Math.min(max, (size.w - pad * 2) / b.w, (size.h - pad * 2) / b.h);
    return { k, x: size.w / 2 - k * (b.x0 + b.w / 2), y: size.h / 2 - k * (b.y0 + b.h / 2) };
  };
  const all = useMemo(() => (size.w ? fit([...base.keys()], 88, 1.2) : { k: 1, x: 0, y: 0 }), [base, size]); // eslint-disable-line react-hooks/exhaustive-deps
  const auto = (focus || group || route) && on && size.w ? (() => { const v = fit([...on], focus ? 130 : 110, 1.5); return v.k < all.k ? all : v; })() : all;
  const view = free ?? auto;

  // roda do mouse aproxima em volta do cursor
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let idle: ReturnType<typeof setTimeout>;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect(); const cx0 = e.clientX - r.left; const cy0 = e.clientY - r.top;
      setMoving(true); clearTimeout(idle); idle = setTimeout(() => setMoving(false), 160);
      setFree((f) => { const v = f ?? view; const k = Math.min(3, Math.max(all.k * 0.7, v.k * Math.exp(-e.deltaY * 0.0016))); return { k, x: cx0 - ((cx0 - v.x) / v.k) * k, y: cy0 - ((cy0 - v.y) / v.k) * k }; });
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => { el.removeEventListener('wheel', wheel); clearTimeout(idle); };
  });
  const drag = useRef<{ x: number; y: number; v: View; moved: boolean } | null>(null);
  // a vez de cada ligação: um passo a cada instante; depois da última, uma pausa e recomeça
  const pulseKey = (pulses ?? []).map((p) => `${p.source}>${p.target}`).join('|');
  const [tick, setTick] = useState(0);
  useEffect(() => { if (!pulseKey) return; setTick(0); const t = setInterval(() => setTick((x) => x + 1), 420); return () => clearInterval(t); }, [pulseKey]);
  const turn = useMemo(() => new Map(pulseKey ? pulseKey.split('|').map((k, i) => [k, i] as const) : []), [pulseKey]);
  const period = turn.size + 7;
  const hubFocus = focus && byId.get(focus)?.kind === 'hub' ? focus : null;

  return (
    <div
      ref={ref}
      className={cx('rr-graph', on && 'has-lit', (focus || group || route) && 'has-focus', view.k > 1.15 && 'is-close', (moving || !ready) && 'is-moving', className)}
      onPointerDown={(e) => { if ((e.target as HTMLElement).closest('.rr-graph__node, .rr-graph__group')) return; drag.current = { x: e.clientX, y: e.clientY, v: view, moved: false }; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); }}
      onPointerMove={(e) => { const d = drag.current; if (!d) return; const mx = e.clientX - d.x; const my = e.clientY - d.y; if (!d.moved && Math.abs(mx) + Math.abs(my) < 5) return; d.moved = true; setMoving(true); setFree({ k: d.v.k, x: d.v.x + mx, y: d.v.y + my }); }}
      onPointerUp={() => { const d = drag.current; drag.current = null; setMoving(false); if (d && !d.moved) onSelect?.(null); }}
    >
      <div className="rr-graph__world" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`, ['--rr-graph-k' as string]: view.k }}>
        <svg className="rr-graph__links" aria-hidden>
          {groups.map((g) => {
            const pts = g.members.filter((id) => !later(byId.get(id)?.since)).map((id) => base.get(id)).filter((p): p is P => Boolean(p));
            if (!pts.length) return null;
            const h = hull(pts); const d = h.length === 1 ? `M${h[0]!.x} ${h[0]!.y}l0.1 0` : `M${h.map((p) => `${p.x} ${p.y}`).join('L')}Z`;
            return <g key={g.id} className={cx('rr-graph__hull', g.id === selected && 'is-on')}><path d={d} className="rr-graph__hull-edge" /><path d={d} className="rr-graph__hull-cut" /><path d={d} className="rr-graph__hull-fill" /></g>;
          })}
          {links.map((l) => {
            const a = pos.get(l.source); const b = pos.get(l.target); if (!a || !b) return null;
            // curva leve, sempre para o mesmo lado em cada ligação
            const bend = ((hash(l.source + l.target) % 9) - 4) * 0.035;
            const mx = (a.x + b.x) / 2 - (b.y - a.y) * bend; const my = (a.y + b.y) / 2 + (b.x - a.x) * bend;
            const step = hop(l);
            const isOn = step >= 0 || cores.includes(l.source) || cores.includes(l.target);
            const d = `M${a.x} ${a.y}Q${mx} ${my} ${b.x} ${b.y}`;
            // a luz que percorre a ligação: na rota, no sentido do caminho e uma depois da outra; no que é novo, do ponto para o centro
            const trace = step >= 0 ? <path key={`t:${route!.join('>')}:${step}`} d={d} pathLength={1} className="rr-graph__trace" style={{ ['--rr-graph-from' as string]: route![step] === l.source ? 1 : -1, animationDelay: `${step * 480}ms` }} />
              : isNew(l.since) ? <path key="n" d={d} pathLength={1} className="rr-graph__trace is-new" style={{ ['--rr-graph-from' as string]: -1 }} /> : null;
            const mine = turn.get(`${l.source}>${l.target}`);
            const round = mine === undefined || tick < mine || later(l.since) ? -1 : Math.floor((tick - mine) / period);
            const pulse = round >= 0 && (tick - mine!) % period < 5 ? <path key={`p${round}`} d={d} pathLength={1} className="rr-graph__pulse" /> : null;
            return <g key={`${l.source}>${l.target}`}><path d={d} className={cx('rr-graph__link', isOn && 'is-on', later(l.since) && 'is-later')} style={{ d: `path("${d}")`, strokeWidth: Math.min(3, 0.8 + ((l.weight ?? 1) - 1) * 0.7) }} />{trace}{pulse}</g>;
          })}
        </svg>
        {groups.map((g) => {
          const pts = g.members.filter((id) => !later(byId.get(id)?.since)).map((id) => base.get(id)).filter((p): p is P => Boolean(p));
          if (!pts.length) return null;
          const top = pts.reduce((a, p) => (p.y < a.y ? p : a)); // o nome fica em cima do contorno, sobre o centro mais alto
          return <button key={g.id} type="button" className={cx('rr-graph__group', g.id === selected && 'is-on')} style={{ left: top.x, top: top.y }} onClick={() => onSelect?.(g.id === selected ? null : g.id)}><span><b>{g.label}</b>{g.sub && <small>{g.sub}</small>}</span></button>;
        })}
        {nodes.map((d) => {
          const p = pos.get(d.id); if (!p) return null;
          const isOn = on?.has(d.id) ?? false;
          const left = Boolean(hubFocus && isOn && d.kind === 'node' && p.x < (pos.get(hubFocus)?.x ?? 0) - 20);
          const inHub = hubFocus && isOn && d.kind === 'node';
          const text = inHub ? note?.(d.id, hubFocus) : undefined;
          const form = (inHub ? shapeIn?.(d.id, hubFocus) : undefined) ?? d.shape ?? 'dot';
          return (
            <button
              key={d.id} type="button" title={d.label}
              className={cx('rr-graph__node', `rr-graph__node--${d.kind}`, form !== 'dot' && `rr-graph__node--${form}`, d.faint && 'is-faint', d.named && 'is-named', !near.get(d.id)?.size && 'is-loose', isOn && 'is-on', left && 'is-left', (cores.includes(d.id) || (route && d.kind === 'hub' && isOn)) && 'is-lit', isNew(d.since) && 'is-new', d.id === focus && 'is-focus', later(d.since) && 'is-later')}
              style={{ left: p.x, top: p.y, ['--rr-graph-w' as string]: d.weight ?? 1 }}
              onMouseEnter={() => setHot(d.id)} onMouseLeave={() => setHot((h) => (h === d.id ? null : h))}
              onClick={() => onSelect?.(d.id === focus ? null : d.id)}
            >
              <span className="rr-graph__dot" />
              <span className="rr-graph__label"><b>{d.label}</b>{d.sub && <small>{d.sub}</small>}{text && <em>{text}</em>}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Faixa de datas: um traço por acontecimento entre duas datas, com os meses marcados. O que está em destaque acende.
 * Com `onCursor`, a faixa vira controle: arrastar escolhe até que dia olhar (`null` = o período inteiro).
 */
export function DateStrip({ from, to, marks, onPick, cursor, onCursor }: { from: string; to: string; marks: { id: string; date: string; on?: boolean; title?: string }[]; onPick?: (id: string) => void; cursor?: string | null; onCursor?: (iso: string | null) => void }) {
  const t0 = Date.parse(from); const t1 = Date.parse(to);
  const at = (iso: string) => `${Math.min(100, Math.max(0, ((Date.parse(iso) - t0) / (t1 - t0 || 1)) * 100))}%`;
  const months: { iso: string; label: string }[] = [];
  for (let d = new Date(`${from.slice(0, 7)}-01T00:00:00Z`); d.getTime() <= t1; d.setUTCMonth(d.getUTCMonth() + 1)) months.push({ iso: d.toISOString().slice(0, 10), label: d.toLocaleDateString('pt-BR', { month: 'short', timeZone: 'UTC' }).replace('.', '') });
  const any = marks.some((m) => m.on);
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const move = (clientX: number) => { const r = ref.current!.getBoundingClientRect(); const f = Math.min(1, Math.max(0, (clientX - r.left) / r.width)); onCursor?.(f > 0.995 ? null : new Date(t0 + f * (t1 - t0)).toISOString().slice(0, 10)); };
  const day = cursor ? new Date(`${cursor}T00:00:00Z`).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', timeZone: 'UTC' }).replace('.', '').replace(' de ', ' ') : null;
  return (
    <div
      ref={ref} className={cx('rr-datestrip', any && 'has-on', onCursor && 'has-cursor')}
      onPointerDown={onCursor ? (e) => { if ((e.target as HTMLElement).closest('.rr-datestrip__mark')) return; dragging.current = true; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); move(e.clientX); } : undefined}
      onPointerMove={onCursor ? (e) => { if (dragging.current) move(e.clientX); } : undefined}
      onPointerUp={() => { dragging.current = false; }}
    >
      <div className="rr-datestrip__line">
        {marks.map((m) => <button key={m.id} type="button" className={cx('rr-datestrip__mark', m.on && 'is-on', cursor && m.date > cursor && 'is-later')} style={{ left: at(m.date) }} title={m.title} onClick={() => onPick?.(m.id)} />)}
      </div>
      <div className="rr-datestrip__months">{months.map((m) => <span key={m.iso} style={{ left: at(m.iso < from ? from : m.iso) }}>{m.label}</span>)}</div>
      {onCursor && <div className={cx('rr-datestrip__cursor', !cursor && 'is-end')} style={{ left: at(cursor ?? to) }}>{day && <span>{day}</span>}</div>}
    </div>
  );
}
